/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import { checkValue } from 'src/components/common/helper';

// Module Context
import { useComponentContext } from 'src/components/common/modules/component-layout/component-context';

// Cpds
import { Cpds } from '@Cpds.Ui';
import { mediaQueryVals, spaces } from 'src/components/common/cpds/token/shared/common';
import { CpdsGridStyleProps } from 'src/components/common/cpds/cpds-grid/typings';
import { getYouTubeVideoId, getVimeoVideoId } from 'src/components/common/cpds/cpds-video';

// Sub Components
import * as V3Breaker from '../styles/v3-breaker-video-module-style';
import { css, ThemeContext } from 'styled-components';
import { themeToken } from 'components/common/cpds/utils/cpds-utils';
import { V3BreakerVideoModuleProps } from 'components/common/modules/v3/brand/v3-breaker-video-module/typings';
import { config } from 'src/config/config';
import CpdsGridSystem from 'src/components/common/cpds/cpds-grid-system/styles';
import useAkamaiVideo from 'components/common/modules/hooks/useAkamaiVideo';

//types
import { CpdsVideoProps } from 'src/components/common/cpds/cpds-video/typings';
import { ClickStreamEventConstants, ClickStreamEventType, clickstreamDataJSON } from 'src/utils/event-wrapper';

const AccordionHeading = ({ expand, backgroundVariant }: { expand: boolean; backgroundVariant: string }) => {
  return (
    <V3Breaker.AccordionHeading
      data-cy="v3-breaker-video-accordion-heading"
      isExpand={expand}
      backgroundVariant={backgroundVariant}
      tag="p"
      className="cpds-meta-roman-book-09"
      data-isclickstreamenabled={true}
      data-clickstreamdata = {clickstreamDataJSON(ClickStreamEventType.INTERACTION,  'breaker_video', 'show_transcript.selected', '', '')}
    >
      Show Transcript
    </V3Breaker.AccordionHeading>
  );
};

export enum BackgroundVariant {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  TERTIARY = 'tertiary',
}

const V3BreakerVideoModule: React.FC<V3BreakerVideoModuleProps> = (props) => {
  const [currentSubtitle, setCurrentSubtitle] = useState<{ time: string; subtitle: string }>({
    time: '00:00:00',
    subtitle: '',
  });
  const [transcript, setTranscript] = useState([]);

  const themeContext: any = useContext(ThemeContext);
  const cpds = themeContext?.styles?.cpds;
  const { token } = themeToken(cpds, cpds?.themeColorMode, props?.themeColorMode);

  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    componentProps: { data, key, id, trackClickStream, ...rest },
  } = useComponentContext();

  const {
    videoUrl,
    video: akamaiVideo,
    videoSubtitleFile,
    image,
    moduleColor: backgroundVariant = BackgroundVariant.PRIMARY,
    playBtnColor,
    fullWidthVideo,
    youtubeId,
    vimeoId,
  } = data;

  const video = checkValue(akamaiVideo) ? useAkamaiVideo(akamaiVideo?.video?.availableResources)?.url : checkValue(videoUrl) ? videoUrl : '';

  const posterUrl = image?.asset?.url || null;

  const fetchSubtitleFile = async (url: string) => {
    try {
      const response = await fetch(url, {
        mode: 'cors',
        headers: {
          Authorization: `Bearer ${config.sanityToken}`, // Replace with your actual token
        },
      });

      if (!response.ok) {
        console.error(`Cannot fetch subtitle file: ${response.status}`);
        throw new Error(`Cannot fetch subtitle file: ${response.status}`);
      }

      const data = await response.json();
      setTranscript(data);
    } catch (error) {
      setTranscript([]);
      console.error(`Error occurred: ${error}`);
    }
  };

  useEffect(() => {
    if (videoSubtitleFile?.asset?.url) {
      fetchSubtitleFile(videoSubtitleFile?.asset?.url);
    }
  }, [videoSubtitleFile?.asset?.url]);

  // Default props for grid can be change according to the design
  const V3BreakerVideoModuleGridStyleProps: CpdsGridStyleProps = {
    padding: `${spaces.space_24} 0`,
    margin: '0 auto',
    width: '100%',
    backGroundColor: `${
      checkValue(backgroundVariant)
        ? backgroundVariant.toLowerCase() === BackgroundVariant.PRIMARY
          ? token?.module_v3_breaker_video_primary_background_color || '#FFFFFF'
          : backgroundVariant.toLowerCase() === BackgroundVariant.SECONDARY
          ? token?.module_v3_breaker_video_secondary_background_color || '#FBF8F1'
          : backgroundVariant.toLowerCase() === BackgroundVariant.TERTIARY
          ? token?.module_v3_breaker_video_tertiary_background_color || '#FDDB32'
          : 'transparent'
        : 'transparent'
    };`,
    mediaQueries: {
      [mediaQueryVals.cpds_small_upper_limit]: { padding: `${spaces.space_16} 0` },
    },
  };

  function convertToSeconds(timeStr) {
    const [minutes, seconds] = timeStr.split('.').map(Number);
    return minutes * 60 + seconds;
  }

  function secondsToHMS(seconds) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    // Pad hours, minutes, and seconds with leading zeros if needed
    const pad = (num) => String(num).padStart(2, '0');

    return `${pad(hours)}:${pad(minutes)}:${pad(secs)}`;
  }

  const onTimeUpdate = (e) => {
    if (!checkValue(transcript, true)) {
      return;
    }

    const currentTime = convertToSeconds(e.currentTime);
    const subtitle = transcript.find((sub) => sub.startTime <= currentTime && currentTime < sub.endTime); // Adjust the timing window as needed
    if (subtitle) {
      const duration = subtitle.endTime - subtitle.startTime;
      const words = subtitle.subtitle.split(' ');
      const durationPerWord = duration / words.length;

      const wordIndex = Math.floor((currentTime - subtitle.startTime) / durationPerWord);

      const formattedSubtitle = words.map((word, index) => {
        if (index <= wordIndex) {
          if (index === wordIndex) {
            return `<span class="highlight animate">${word}</span>`;
          }
          return `<span class="highlight">${word}</span>`;
        } else {
          return word;
        }
      });
      setCurrentSubtitle({ time: secondsToHMS(currentTime), subtitle: formattedSubtitle.join(' ') });
    } else {
      setCurrentSubtitle({ time: secondsToHMS(currentTime), subtitle: '' });
    }
  };

  const moduleCss = () => {
    return ` 
          
      .v3-breaker-video-module-grid {
        .grid-item-video {
          width: 100%;
        }
        
        .grid-item-transcript {
          width: 100%;
          
          .cpds-accordion-heading {
            width: fit-content; 
          }
        }
      }
    `;
  };

  const videoProps: CpdsVideoProps = {
    videoUrl: video,
    exClass: 'v3-video-breaker-component',
    autoPlay: false,
    muted: false,
    loop: true,
    youtubeId: youtubeId,
    vimeoId: vimeoId,
    poster: posterUrl,
    akamaiVideo: akamaiVideo,
    playsInline: true,
    heroScrollDownArrow: false,
    hideVideoPlayControls: 'False',
    hideSoundControlsForVideo: false,
    customControlComponnet: Cpds.PlayButton.WithSoundsControls,
    onTimeUpdate,
    playBtnColor,
    aspectRatio: '16:9',
    styleOverrides: css`
      object-fit: cover;
      object-position: center center;
    `,
    trackClickVideo : trackClickStream,
    eventCategory: 'breaker_video',
  };

  if (!checkValue(video) && !checkValue(getYouTubeVideoId(youtubeId)) && !checkValue(getVimeoVideoId(vimeoId))) {
    return;
  }

  return (
    <Cpds.Layout
      className="v3-container v3-breaker-video-module"
      HtmlTag="section"
      id={checkValue(id) ? id : key?.next()}
      css={css`
        ${moduleCss()}
      `}
    >
      <Cpds.GridContainer
        exClasses="grid-container v3-breaker-video-module-grid"
        gridStyleProps={V3BreakerVideoModuleGridStyleProps}
      >
        <CpdsGridSystem.ContainerWrapper>
          <CpdsGridSystem.Container>
            <CpdsGridSystem.Item
              gridColumn={{
                xl: { start: '3', span: '8' },
                md: { start: '0', span: '12' },
                sm: { start: '0', span: '6' },
              }}
            >
              <Cpds.GridItem exClasses="grid-item-video">
                <div className="v3-video-wrapper">
                  <Cpds.Video {...videoProps} />
                </div>
              </Cpds.GridItem>
              {checkValue(transcript, true) && !getYouTubeVideoId(youtubeId) && !getVimeoVideoId(vimeoId) && (
                <Cpds.GridItem exClasses="grid-item-transcript">
                  <Cpds.Accordion
                    type="CUSTOM"
                    defaultBorders={false}
                    chevronIconSize={{
                      width: '8',
                      height: '4.94',
                    }}
                    heading={(expand) => {
                      return <AccordionHeading expand={expand} backgroundVariant={backgroundVariant} />;
                    }}
                  >
                    <V3Breaker.AccordionBody>
                      <V3Breaker.Timestamp
                        backgroundVariant={backgroundVariant}
                        tag="p"
                        className="cpds-meta-roman-book-09"
                        data-cy="v3-breaker-video-accordion-body-timestamp"
                      >
                        {currentSubtitle.time}
                      </V3Breaker.Timestamp>
                      {currentSubtitle.subtitle && (
                        <V3Breaker.Transcript
                          backgroundVariant={backgroundVariant}
                          tag="p"
                          className="cpds-meta-italics-book-09"
                          data-cy="v3-breaker-video-accordion-body-transcript"
                        >
                          {currentSubtitle.subtitle}
                        </V3Breaker.Transcript>
                      )}
                    </V3Breaker.AccordionBody>
                  </Cpds.Accordion>
                </Cpds.GridItem>
              )}
            </CpdsGridSystem.Item>
          </CpdsGridSystem.Container>
        </CpdsGridSystem.ContainerWrapper>
      </Cpds.GridContainer>
    </Cpds.Layout>
  );
};

export default V3BreakerVideoModule;
